/**
 * InfinitSales - Login Page
 * Modern design with inline styles and theme support
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Loader2, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { colors, theme } = useTheme();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(formData.email, formData.password);
      navigate('/');  // SmartRedirect vai decidir o destino correto
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao fazer login. Verifique suas credenciais.');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      display: 'flex',
      minHeight: '100vh',
      backgroundColor: colors.bgSecondary,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif",
    },
    branding: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: '48px',
      backgroundColor: colors.bgPrimary,
      borderRight: `1px solid ${colors.borderColor}`,
      position: 'relative',
      overflow: 'hidden',
    },
    brandingBg: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `radial-gradient(circle at 30% 20%, ${colors.accentPrimary}15 0%, transparent 50%)`,
      pointerEvents: 'none',
    },
    brandingContent: {
      position: 'relative',
      zIndex: 1,
      maxWidth: '480px',
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '48px',
      cursor: 'pointer',
    },
    logoIcon: {
      width: '48px',
      height: '48px',
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `1px solid ${colors.borderColor}`,
      overflow: 'hidden',
    },
    logoText: {
      fontSize: '24px',
      fontWeight: 700,
      color: colors.textPrimary,
    },
    title: {
      fontSize: '42px',
      fontWeight: 700,
      lineHeight: 1.2,
      marginBottom: '24px',
      color: colors.textPrimary,
    },
    titleGradient: {
      background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, #34d399 100%)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    description: {
      fontSize: '18px',
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: '48px',
    },
    checklist: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    checkItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      fontSize: '15px',
      color: colors.textPrimary,
    },
    checkIcon: {
      width: '24px',
      height: '24px',
      backgroundColor: `${colors.accentPrimary}20`,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.accentPrimary,
      flexShrink: 0,
    },
    formContainer: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '48px',
    },
    formWrapper: {
      width: '100%',
      maxWidth: '400px',
    },
    formHeader: {
      marginBottom: '32px',
      textAlign: 'center',
    },
    formTitle: {
      fontSize: '28px',
      fontWeight: 700,
      color: colors.textPrimary,
      marginBottom: '8px',
    },
    formSubtitle: {
      fontSize: '15px',
      color: colors.textSecondary,
    },
    error: {
      padding: '12px 16px',
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '8px',
      color: '#ef4444',
      fontSize: '14px',
      marginBottom: '24px',
      textAlign: 'center',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    label: {
      fontSize: '14px',
      fontWeight: 500,
      color: colors.textPrimary,
    },
    inputWrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    inputIcon: {
      position: 'absolute',
      left: '14px',
      color: colors.textTertiary,
      pointerEvents: 'none',
      transition: 'color 0.2s',
    },
    input: {
      width: '100%',
      padding: '14px 14px 14px 46px',
      backgroundColor: colors.bgPrimary,
      border: `2px solid ${colors.borderColor}`,
      borderRadius: '10px',
      fontSize: '15px',
      color: colors.textPrimary,
      outline: 'none',
      transition: 'border-color 0.2s, box-shadow 0.2s',
    },
    inputFocused: {
      borderColor: colors.accentPrimary,
      boxShadow: `0 0 0 3px ${colors.accentPrimary}20`,
    },
    inputAction: {
      position: 'absolute',
      right: '12px',
      padding: '6px',
      border: 'none',
      background: 'transparent',
      color: colors.textTertiary,
      cursor: 'pointer',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    submitBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '14px 24px',
      backgroundColor: colors.accentPrimary,
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      fontSize: '15px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'background-color 0.2s, transform 0.1s',
      marginTop: '8px',
    },
    submitBtnDisabled: {
      opacity: 0.7,
      cursor: 'not-allowed',
    },
    footer: {
      marginTop: '32px',
      textAlign: 'center',
      fontSize: '14px',
      color: colors.textSecondary,
    },
    link: {
      color: colors.accentPrimary,
      textDecoration: 'none',
      fontWeight: 500,
    },
    mobileHeader: {
      display: 'none',
    },
  };

  // Add responsive styles
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 900;

  if (isMobile) {
    styles.branding.display = 'none';
    styles.formContainer.padding = '24px';
  }

  return (
    <div style={styles.container}>
      {/* Left Side - Branding */}
      <div style={styles.branding}>
        <div style={styles.brandingBg} />
        <div style={styles.brandingContent}>
          <Link to="/home" style={{ textDecoration: 'none' }}>
            <div style={styles.logo}>
              <div style={styles.logoIcon}>
                <img src={process.env.PUBLIC_URL + '/logo.jpeg'} alt="InfinitSales" style={{ width: '34px', height: '34px', objectFit: 'contain' }} />
              </div>
              <span style={styles.logoText}>InfinitSales</span>
            </div>
          </Link>

          <h1 style={styles.title}>
            WhatsApp inteligente
            <br />
            <span style={styles.titleGradient}>para seu negócio</span>
          </h1>

          <p style={styles.description}>
            Gerencie suas conversas, automatize respostas e escale suas vendas.
            Tudo em uma única plataforma.
          </p>

          <div style={styles.checklist}>
            {[
              'Inbox unificado com todas as conversas',
              'Respostas automáticas com Agentes IA',
              'Seguro e confiável — seus dados protegidos',
              'Follow-ups inteligentes e reengajamento',
              'Conecte via QR Code ou Business API',
              'Configuração em menos de 5 minutos',
            ].map((item, index) => (
              <div key={index} style={styles.checkItem}>
                <div style={styles.checkIcon}>
                  <Check size={14} />
                </div>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div style={styles.formContainer}>
        <div style={styles.formWrapper}>
          {/* Mobile Logo */}
          {isMobile && (
            <Link to="/home" style={{ textDecoration: 'none' }}>
              <div style={{ ...styles.logo, justifyContent: 'center', marginBottom: '32px' }}>
                <div style={styles.logoIcon}>
                  <img src={process.env.PUBLIC_URL + '/logo.jpeg'} alt="InfinitSales" style={{ width: '34px', height: '34px', objectFit: 'contain' }} />
                </div>
                <span style={styles.logoText}>InfinitSales</span>
              </div>
            </Link>
          )}

          <div style={styles.formHeader}>
            <h2 style={styles.formTitle}>Bem-vindo de volta</h2>
            <p style={styles.formSubtitle}>Entre para acessar sua conta</p>
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.inputGroup}>
              <label style={styles.label} htmlFor="email">E-mail</label>
              <div style={styles.inputWrapper}>
                <Mail 
                  size={18} 
                  style={{
                    ...styles.inputIcon,
                    color: focusedField === 'email' ? colors.accentPrimary : colors.textTertiary
                  }} 
                />
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="seu@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  required
                  autoComplete="email"
                  style={{
                    ...styles.input,
                    ...(focusedField === 'email' ? styles.inputFocused : {}),
                  }}
                />
              </div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label} htmlFor="password">Senha</label>
              <div style={styles.inputWrapper}>
                <Lock 
                  size={18} 
                  style={{
                    ...styles.inputIcon,
                    color: focusedField === 'password' ? colors.accentPrimary : colors.textTertiary
                  }}
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  required
                  autoComplete="current-password"
                  style={{
                    ...styles.input,
                    paddingRight: '46px',
                    ...(focusedField === 'password' ? styles.inputFocused : {}),
                  }}
                />
                <button
                  type="button"
                  style={styles.inputAction}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button 
              type="submit" 
              style={{
                ...styles.submitBtn,
                ...(loading ? styles.submitBtnDisabled : {}),
              }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  Entrando...
                </>
              ) : (
                <>
                  Entrar
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          <div style={styles.footer}>
            <p>
              Ainda não tem uma conta?{' '}
              <Link to="/register" style={styles.link}>
                Criar conta grátis
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        input::placeholder {
          color: ${colors.textTertiary};
        }
        @media (max-width: 900px) {
          .auth-branding {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Login;